import { getLocalTimeZone, today } from "@internationalized/date";
import { t } from "@travily/i18n";
import { StandardButton } from "@ui/components/StandardButton";
import { StandardRangeCalendar } from "@ui/components/StandardRangeCalendar";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { Dialog } from "@ui/primitives/Modal";
import { DialogTrigger, Popover } from "@ui/primitives/Popover";
import type { DateValue, RangeValue } from "@ui/primitives/shared";
import { useState } from "react";
import { AvailabilityItem } from "./AvailabilityItem";
import { formatDayWithWeekday } from "./format";

// The flexible ("anytime") half of the availability card: Check in / Check out
// read as two stacked items but form ONE popover trigger — a button cannot
// contain interactive children, and a single trigger sidesteps the duplicate
// trigger-id wiring a raw DateRangePicker would need. Picking a complete range
// closes the calendar and arms the enquiry CTA.
const flexibleDatesPicker = tv({
  slots: {
    trigger: [
      "flex w-full flex-col gap-3 rounded-xl text-left",
      "outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2",
    ],
    popover: [
      "drop-shadow-popover",
      "entering:fade-in entering:zoom-in-95 entering:animate-in entering:duration-150 entering:ease-out",
      "exiting:fade-out exiting:zoom-out-95 exiting:animate-out exiting:duration-100 exiting:ease-in",
    ],
    card: "flex flex-col overflow-hidden rounded-3xl bg-white p-4 outline-none",
  },
});

export interface FlexibleDatesPickerProps {
  /** `${messagesPath()}?to=<handle>` — the CTA appends `&draft=`. */
  messageHref: string;
  className?: string;
}

export function FlexibleDatesPicker({ messageHref, className }: FlexibleDatesPickerProps) {
  const styles = flexibleDatesPicker();
  const [range, setRange] = useState<RangeValue<DateValue> | null>(null);
  const [isOpen, setOpen] = useState(false);

  const selectDate = t("components.views.programs.show.availability_select_date");
  const cta = t("components.views.programs.show.availability_cta");
  const draft =
    range &&
    t("components.views.programs.show.availability_draft_anytime", {
      start: formatDayWithWeekday(range.start),
      end: formatDayWithWeekday(range.end),
    });

  return (
    <div className={className}>
      <DialogTrigger isOpen={isOpen} onOpenChange={setOpen}>
        <Button className={styles.trigger()}>
          <AvailabilityItem
            state={range ? "green" : "default"}
            title={t("components.views.programs.show.availability_check_in")}
            body={range ? formatDayWithWeekday(range.start) : selectDate}
          />
          <AvailabilityItem
            state={range ? "green" : "default"}
            title={t("components.views.programs.show.availability_check_out")}
            body={range ? formatDayWithWeekday(range.end) : selectDate}
          />
        </Button>
        <Popover className={styles.popover()} offset={8} placement="bottom">
          <Dialog aria-label={t("components.views.programs.show.availability_picker_label")} className={styles.card()}>
            <StandardRangeCalendar
              minValue={today(getLocalTimeZone())}
              value={range}
              onChange={(next) => {
                setRange(next);
                setOpen(false);
              }}
            />
          </Dialog>
        </Popover>
      </DialogTrigger>

      {draft ? (
        <StandardButton
          color="pink"
          size="44"
          className="mt-4 w-full"
          href={`${messageHref}&draft=${encodeURIComponent(draft)}`}
        >
          {cta}
        </StandardButton>
      ) : (
        <StandardButton isDisabled color="pink" size="44" className="mt-4 w-full">
          {cta}
        </StandardButton>
      )}
    </div>
  );
}
